import { isSafeHostname } from '@johnwhiteside/social-web-sdk'

/**
 * Bounded, origin-pinned fetch helper for direct requests to Mastodon/Pixelfed
 * instance hosts (pagination cursors, account lookups).
 *
 * Threat model:
 * - `instanceUrl`/pagination `nextUrl` values originate from a user-typed
 *   handle or from server-supplied Link headers attached to a bearer token. A
 *   hostile hostname can steer authenticated, in-browser requests at internal
 *   network targets (`localhost`, `169.254.169.254`, RFC1918 ranges) or at an
 *   arbitrary third-party host that then receives the session's
 *   `Authorization` header.
 * - `assertSafeMastodonUrl`/`canonicalizeInstanceOrigin` reject literal
 *   private/loopback/link-local/metadata hostnames and IPs via the SDK's
 *   `isSafeHostname` (v0.5.1+, closes trailing-dot and hex-form
 *   IPv4-mapped/NAT64 IPv6 bypasses — mackinac#2092). `expectedOrigin` pins a
 *   fetch to a given instance's own origin so a compromised instance cannot
 *   redirect the bearer token to an off-origin host via a crafted Link
 *   header.
 * - The browser's `fetch` has no default timeout. `mastodonFetchJsonWithStatus`
 *   bounds the fetch AND the JSON body read under the same timer, so a
 *   stalled body read after a prompt 200 is not left unbounded.
 *
 * Explicitly NOT covered (narrowed claims — do not extend without review):
 * - DNS rebinding: a public hostname that resolves to a private address at
 *   request time is not detected by a literal hostname check.
 * - Cross-origin redirects: browser `fetch` follows redirects by default and
 *   the `Location` target is never re-validated through
 *   `assertSafeMastodonUrl` — a compromised instance can point a validated,
 *   origin-pinned URL at a private/loopback target via a 3xx response. Not
 *   confined here — tracked separately as mackinac#2100.
 */

export const DEFAULT_TIMEOUT_MS = 10_000

// Requires the `//` authority marker so a bare `host:port` value (e.g.
// `example.com:8443`) is never mistaken for a schemed URL — RFC 3986 scheme
// syntax permits dots, which would otherwise make "example.com:" look like
// a scheme.
const SCHEME_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//

export class UnsafeUrlError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'UnsafeUrlError'
    }
}

function isAbortError(err: unknown): boolean {
    return err instanceof Error && err.name === 'AbortError'
}

function makeAbortError(): Error {
    const err = new Error('The operation was aborted')
    err.name = 'AbortError'
    return err
}

/**
 * Canonicalize a user-typed or stored Mastodon/Pixelfed instance value into a
 * safe `https://` origin plus a stable key.
 *
 * - Prepends `https://` only when no scheme is present; an explicitly
 *   schemed non-https input (`http://`, `ftp://`, `://host`) is rejected,
 *   never reinterpreted as a bare hostname.
 * - Rejects credentials, non-root paths, query strings, and fragments —
 *   instance URLs here are always origin-only.
 * - Rejects unsafe hostnames (private/loopback/link-local/metadata/etc) via
 *   the SDK's `isSafeHostname`.
 * - Strips all trailing dots from the hostname so origin comparison and
 *   `isSafeHostname` share one host identity (`https://example.com.`
 *   canonicalizes to origin `https://example.com`).
 * - `key` uses the host (hostname + non-default port) so a non-default port
 *   is preserved.
 */
export function canonicalizeInstanceOrigin(input: string): { origin: string; key: string } {
    if (typeof input !== 'string' || !input.trim()) {
        throw new UnsafeUrlError('Instance URL is empty')
    }

    const trimmed = input.trim()
    const hasScheme = SCHEME_RE.test(trimmed)
    if (hasScheme && !/^https:\/\//i.test(trimmed)) {
        throw new UnsafeUrlError(`Unsafe instance URL scheme: ${trimmed}`)
    }

    const candidate = hasScheme ? trimmed : `https://${trimmed}`

    let parsed: URL
    try {
        parsed = new URL(candidate)
    } catch {
        throw new UnsafeUrlError(`Malformed instance URL: ${trimmed}`)
    }

    if (parsed.protocol !== 'https:') {
        throw new UnsafeUrlError(`Unsafe instance URL scheme: ${trimmed}`)
    }
    if (parsed.username || parsed.password) {
        throw new UnsafeUrlError('Instance URL must not contain credentials')
    }
    if (parsed.pathname !== '' && parsed.pathname !== '/') {
        throw new UnsafeUrlError('Instance URL must not contain a path')
    }
    if (parsed.search) {
        throw new UnsafeUrlError('Instance URL must not contain a query string')
    }
    if (parsed.hash) {
        throw new UnsafeUrlError('Instance URL must not contain a fragment')
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new UnsafeUrlError(`Unsafe instance hostname: ${parsed.hostname}`)
    }

    const strippedHostname = parsed.hostname.replace(/\.+$/, '')
    if (strippedHostname !== parsed.hostname) {
        parsed.hostname = strippedHostname
    }

    return { origin: parsed.origin, key: parsed.host }
}

/**
 * Guard a URL (and optionally pin it to an expected origin) before it is
 * used in a request that will carry a bearer token. Throws `UnsafeUrlError`
 * on any parse/protocol/hostname/origin failure — never a native `TypeError`.
 */
export function assertSafeMastodonUrl(url: string, expectedOrigin?: string): URL {
    let parsed: URL
    try {
        parsed = new URL(url)
    } catch {
        throw new UnsafeUrlError(`Malformed URL: ${url}`)
    }

    if (parsed.protocol !== 'https:') {
        throw new UnsafeUrlError(`Unsafe URL scheme: ${url}`)
    }
    if (parsed.username || parsed.password) {
        throw new UnsafeUrlError('URL must not contain credentials')
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new UnsafeUrlError(`Unsafe hostname: ${parsed.hostname}`)
    }

    if (expectedOrigin !== undefined) {
        let expected: URL
        try {
            expected = new URL(expectedOrigin)
        } catch {
            throw new UnsafeUrlError(`Malformed expected origin: ${expectedOrigin}`)
        }
        if (parsed.origin !== expected.origin) {
            throw new UnsafeUrlError(
                `URL origin ${parsed.origin} does not match expected ${expected.origin}`
            )
        }
    }

    return parsed
}

export interface MastodonFetchOptions {
    timeoutMs?: number
    expectedOrigin?: string
}

function validateTimeoutMs(timeoutMs: number): void {
    if (!Number.isInteger(timeoutMs) || timeoutMs <= 0) {
        throw new RangeError(`timeoutMs must be a finite positive integer, got ${timeoutMs}`)
    }
}

/**
 * Compose a caller-supplied signal (if any) with an internal timeout signal,
 * returning the combined signal plus a cleanup function. Never mutates or
 * overwrites the caller's own signal.
 */
function composeSignal(
    callerSignal: AbortSignal | null | undefined,
    timeoutMs: number
): { signal: AbortSignal; cleanup: () => void } {
    const timeoutController = new AbortController()
    const timer = setTimeout(() => timeoutController.abort(makeAbortError()), timeoutMs)

    if (!callerSignal) {
        return {
            signal: timeoutController.signal,
            cleanup: () => clearTimeout(timer),
        }
    }

    const AbortSignalAny = (
        AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }
    ).any
    if (typeof AbortSignalAny === 'function') {
        const combined = AbortSignalAny([callerSignal, timeoutController.signal])
        return {
            signal: combined,
            cleanup: () => clearTimeout(timer),
        }
    }

    if (callerSignal.aborted) {
        timeoutController.abort(callerSignal.reason ?? makeAbortError())
    }

    const onCallerAbort = () => timeoutController.abort(callerSignal.reason ?? makeAbortError())
    callerSignal.addEventListener('abort', onCallerAbort)
    return {
        signal: timeoutController.signal,
        cleanup: () => {
            clearTimeout(timer)
            callerSignal.removeEventListener('abort', onCallerAbort)
        },
    }
}

export type MastodonFetchJsonResult<T> =
    | { ok: true; status: number; data: T; linkHeader: string | null }
    | { ok: false; status: number; data: undefined; linkHeader: string | null }

/**
 * Validated, timeout-bounded `fetch` plus JSON body parsing under the same
 * controller/timer — the timer is cleared only after the body read settles,
 * so this fully bounds a stalled body read (not just time-to-headers).
 *
 * Validates `assertSafeMastodonUrl(url, expectedOrigin)` immediately before
 * `fetch` — this is the network-boundary enforcement point; call sites that
 * also validate at parse time (e.g. a Link header cursor) are adding a
 * second, earlier layer, not relying on this as the only check.
 *
 * The body is parsed only when the response is `ok` — a non-JSON error page
 * from a broken proxy surfaces as `{ ok: false, status }` rather than a
 * confusing JSON parse error in place of the caller's own HTTP-status error.
 * `linkHeader` exposes the `Link` response header for callers that paginate
 * via it; it is `null` (not `undefined`) when absent.
 */
export async function mastodonFetchJsonWithStatus<T>(
    url: string,
    init: RequestInit = {},
    opts: MastodonFetchOptions = {}
): Promise<MastodonFetchJsonResult<T>> {
    const { timeoutMs = DEFAULT_TIMEOUT_MS, expectedOrigin } = opts
    validateTimeoutMs(timeoutMs)

    assertSafeMastodonUrl(url, expectedOrigin)

    const callerSignal = init.signal
    if (callerSignal?.aborted) {
        throw makeAbortError()
    }

    const { signal, cleanup } = composeSignal(callerSignal, timeoutMs)
    try {
        const res = await fetch(url, { ...init, signal })
        const linkHeader = res.headers.get('Link')
        if (!res.ok) {
            return { ok: false, status: res.status, data: undefined, linkHeader }
        }
        const data = (await res.json()) as T
        return { ok: true, status: res.status, data, linkHeader }
    } catch (err) {
        if (isAbortError(err)) {
            throw makeAbortError()
        }
        throw err
    } finally {
        cleanup()
    }
}

/**
 * Pure retry predicate shared by the global query client: never retry a
 * validation failure (the request is not going to become safe on retry),
 * otherwise retry once.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
    return !(error instanceof UnsafeUrlError) && failureCount < 1
}
